<template>
  <div class="container client-note">
    <div class="client-note__title-container">
      <div class="client-note__title-left">
        <div class="client-note__header-button-wrapper"
             v-if="isDataFetched">
          <button
            @click.stop="cancelSortingAndFilters"
            class="client-note__clear-filters">
            <span>clear all filters and sorting</span>
            <span class="client-note__cross">&times;</span>
          </button>
        </div>

      </div>
      <div class="client-note__title-right"
           v-if="isDataFetched">
        <div class="client-note__client-notes">
          <span class="client-note__count">Project Notes: {{totalItems}}</span>
          <base-select
            class="client-note__grid-select"
            alignLeft
            showTopBorder
            shiftLabelRight
            :options="paginationOptions"
            :titleStyle="{'padding-left': '5px'}"
            v-model="pageItemsLimit"
            :selectStyle="{'max-height': '35px !important', width: '100px !important'}"
            @change="handleLimitChange"
          />
        </div>
        <button class="client-note__button" @click.prevent="toogleCreateModal(null, true)">
          Create Project Note
        </button>
      </div>
    </div>

    <div class="common-table client-note__table">
      <div class="common-table__row common-table__header"
           v-if="isDataFetched">
        <div class="common-table__header-cell"
             v-for="column in clientNoteTableColumnsConfig"
             :key="column.property"
             :class="{[`client-note__${column.property}`]: true}">
          <base-multi-select-sortable-header
            v-if="column.property !== 'edit-button'"
            class="client-note__select-header"
            :column="column"
            :sort-config="sortConfig"
            :predefinedOptions="filterParams[column.property]"
            :predefinedDateRangeFilter="['created', 'updated'].includes(column.property) ? {start: filterParams[column.property]['date_from'], end: filterParams[column.property]['date_to']}: null"
            @sort="onHeaderCellClicked(column, $event)"
            @apply="filterColumn($event, column)"
            @cancel="cancelFilter($event, column)"
            :ref="sortableHeaderRefs[column.property]"
          />
        </div>
      </div>
      <template v-if="dataMissing">
        <div class="common-table__row no-data-block">
          No records found
        </div>
      </template>
      <div class="" v-else-if="!isDataFetched">
        <div class="client-note__loader">
          <base-loader/>
        </div>
      </div>
      <template v-else>
        <template v-for="(clientNote) in paginatedClientNotes">
          <div :key="clientNote.id" class="common-table__row">
            <div class="common-table__cell"
                 v-for="column in clientNoteTableColumnsConfig"
                 :key="column.property"
                 :class="[`client-note__${column.property}`, `client-note__row_${column.property}`]">
              <template v-if="column.property === 'edit-button'">
                <a @click="toogleCreateModal(clientNote.id)" class="edit-icon">
                  <base-material-design-icon icon="edit"/>
                </a>
              </template>
              <template v-else-if="column.property === 'is_active'">
                  <span>
                    {{ clientNote[column.property] ? 'Yes' : 'No' }}
                  </span>
              </template>
              <template v-else>
                  <span>
                    {{ clientNote[column.property] }}
                  </span>
              </template>
            </div>
          </div>
        </template>
      </template>
    </div>
    <div class="pagination-wrapper" v-show="hasPagination">
      <base-pagination :limit="pageItemsLimit"
                       :total-items="totalItems"
                       @pageChanged="pageChanged"/>
    </div>
    <div class="client-note__update-loader" v-if="showUpdateLoader">
      <base-loader class="client-note__update-loader-ring"/>
    </div>

    <modal name="create"
           v-bind="changeClientNoteModalProps">
      <CreateUpdateClientNotePopup
        ref="create-client-note"
        @apply="createOrUpdateClientNote($event)"
        @delete="deleteClientNote($event)"
        :selected-client-note="selectedClientNote"
        :opts="opts"
        @cancel="toogleCreateModal"/>
    </modal>
  </div>
</template>

<script>
import helper from '@/services/Helper'
import { mapActions, mapState } from 'vuex'
import baseModalProps from '@/common/BaseModalProps'
import CreateUpdateClientNotePopup from './CreateUpdateClientNotePopup'
import _ from 'lodash'
import BaseSortMixins from '@/common/BaseSortMixins'

const ITEMS_PER_PAGE = 100
const EMPTY_FILTERS = {
  name: '',
  category: [],
  created: {
    date_from: null,
    date_to: null
  },
  updated: {
    date_from: null,
    date_to: null
  },
  sort_column: [],
  sort_order: [],
  page_from: 0,
  page_to: ITEMS_PER_PAGE
}
// table config
const DEFAULT_CONFIG = [
  {
    label: '',
    property: 'edit-button'
  },
  {
    label: 'Name',
    property: 'name',
    isDefaultSort: true // this column will be sorted by default
  },
  {
    label: 'Category',
    property: 'category',
    options: []
  },
  {
    label: 'Created',
    property: 'created'
  },
  {
    label: 'Updated',
    property: 'updated'
  }
]
const PAGINATION_OPTIONS = [
  {
    label: '100',
    value: 100
  },
  {
    label: '250',
    value: 250
  },
  {
    label: '500',
    value: 500
  }
]
export default {
  name: 'ClientNote',
  components: { CreateUpdateClientNotePopup },
  mixins: [BaseSortMixins],
  mounted () {
  },
  data () {
    return {
      isDataFetched: false,
      selectedClientNote: null,
      isDataUpdating: false,
      sortConfig: [],
      pageItemsLimit: ITEMS_PER_PAGE,
      totalItems: 0,
      clientNoteTableColumnsConfig: _.cloneDeep(DEFAULT_CONFIG),
      filterParams: _.cloneDeep(EMPTY_FILTERS),
      sortableHeaderRefs: {
        'name': 'name',
        'category': 'category',
        'created': 'created',
        'updated': 'updated'
      },
      paginationOptions: [...PAGINATION_OPTIONS]
    }
  },
  watch: {
    settings: {
      handler (settings) {
        if (settings.categories) {
          const categories = settings.categories.map(helper.convertListToOptions)
          this.clientNoteTableColumnsConfig.find(item => item.property === 'category').options = categories
        }
      },
      deep: true
    },
    clientId: {
      handler (v) {
        if (v) {
          this.filterParams = _.cloneDeep(EMPTY_FILTERS)
          this.loadData()
        }
      },
      immediate: true
    }
  },
  computed: {
    ...mapState('clientNote', {
      paginatedClientNotes (state) {
        let clientNotes = _.cloneDeep(state.clientNotes)
        // eslint-disable-next-line camelcase
        const { page_from = 0, page_to = 100 } = this.filterParams
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.totalItems = clientNotes.length
        return clientNotes.slice(page_from, page_to)
      },
      opts: 'options',
      settings: 'settings'
    }),
    dataMissing () {
      return this.isDataFetched && !this.paginatedClientNotes.length
    },
    changeClientNoteModalProps () {
      const changeClientNoteModalProps = {
        width: 750
      }
      return { ...baseModalProps, ...changeClientNoteModalProps }
    },
    hasPagination () {
      return this.isDataFetched && !this.isDataUpdating && this.totalItems > this.pageItemsLimit
    },
    showUpdateLoader () {
      return this.isDataFetched && this.isDataUpdating
    }
  },
  methods: {
    ...mapActions('clientNote', [
      'getClientNoteData',
      'createClientNote',
      'deleteClientNoteById',
      'updateClientNote'
    ]),
    async loadData () {
      this.isDataUpdating = true
      await this.getClientNoteData({ clientId: this.clientId, filters: this.filterParams })
      this.isDataFetched = true
      this.isDataUpdating = false
    },
    reset () {
      this.sortConfig = []
      this.totalItems = 0
      this.clientNoteTableColumnsConfig = _.cloneDeep(DEFAULT_CONFIG)
      this.loadPageData()
    },
    pageChanged ({ from, to }) {
      this.filterParams.page_from = from
      this.filterParams.page_to = to
    },
    async createOrUpdateClientNote (clientNote) {
      this.isDataUpdating = true
      try {
        if (clientNote.id && clientNote.id > -1) {
          await this.updateClientNote({ clientId: this.clientId, clientNote })
        } else {
          await this.createClientNote({ clientId: this.clientId, clientNote })
        }
        await this.loadData()
        this.toogleCreateModal()
      } catch (e) {
        this.$refs['create-client-note'].showErrors(e[0])
      } finally {
        this.isDataUpdating = false
      }
    },
    async deleteClientNote (clientNoteId) {
      this.isDataUpdating = true
      try {
        await this.deleteClientNoteById({ clientId: this.clientId, clientNoteId })
        await this.loadData()
        this.toogleCreateModal()
      } catch (e) {
        this.$refs['create-client-note'].showErrors(e[0])
      } finally {
        this.isDataUpdating = false
      }
    },
    toogleCreateModal (id = null, createNew = false) {
      this.selectedClientNote = id ? this.paginatedClientNotes.find(i => i.id === id) : null
      if (id || createNew) {
        this.$modal.show('create')
      } else {
        this.$modal.hide('create')
      }
    },
    handleLimitChange (value) {
      this.pageItemsLimit = value
      this.filterParams = { ...this.filterParams, page_from: 0, page_to: value }
    },
    filterColumn (event, column) {
      if (['created', 'updated'].includes(column.property)) {
        this.filterParams[column.property].date_from = event.date_from
        this.filterParams[column.property].date_to = event.date_to
      } else {
        this.filterParams[column.property] = event
      }
      this.filterParams.page_from = 0
      this.filterParams.page_to = this.pageItemsLimit
      this.loadData()
    },
    onHeaderCellClicked (cell, direction, declineCall) {
      if (direction === 'clear') {
        this.sortConfig.shift()
        return
      }
      if (this.sortConfig.length) {
        this.sortConfig[0].propertyName = cell.property
        this.sortConfig[0].sortDirection = direction
      } else if (cell) {
        this.sortConfig = [{
          sortDirection: direction,
          propertyName: cell.property
        }]
      }
      this.filterParams = {
        ...this.filterParams,
        sort_column: this.sortConfig.map(item => item.propertyName),
        sort_order: this.sortConfig.map(item => item.sortDirection)
      }
      !declineCall && this.loadData()
    },
    cancelSortingAndFilters () {
      const emptyFilters = _.cloneDeep(EMPTY_FILTERS)
      this.filterParams = { ...this.filterParams, ...emptyFilters, page_to: this.pageItemsLimit }
      Object.keys(this.sortableHeaderRefs)
        .forEach(ref => {
          const [element] = this.$refs[ref]
          element.$refs[element.childRef].cancel(true)
        })
      this.sortConfig = []
      !this.isDataUpdating && this.loadData()
    },
    cancelFilter (event, column) {
      if (['created', 'updated'].includes(column.property)) {
        this.filterParams[column.property].date_from = null
        this.filterParams[column.property].date_to = null
      } else {
        this.filterParams[column.property] = null
      }
      this.loadData()
    }
  }
}
</script>

<style scoped lang="scss">

.common-table {
  border-radius: $rfx-default-border-radius;
  overflow: visible;

  &__header {
    background-color: rgba(1, 22, 39, 0.1);
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;
  }

  &__header-cell {
    color: $rfx-dark-march;
    font-weight: bold;
    padding: 0;
    height: 60px;
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  &__cell {
    color: $rfx-dark-march;
    height: 60px;
    padding: 0;
    display: flex;
    align-items: center;
    font-size: 14px;
    border-bottom: 1px solid $rfx-light-steel;

    &:first-child {
      padding-left: 25px;
    }
  }

  &__row {
    border-left: 1px solid $rfx-light-steel;
    border-right: 1px solid $rfx-light-steel;
  }
}

.client-note {
  position: relative;
  min-height: 200vh !important;

  &__keyword-header {
    background-color: rgba(1, 22, 39, 0.1);
  }

  &__cross {
    font-size: 21px;
    margin-left: 5px;
    margin-top: -2px;
  }

  &__title-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1 1 50%;
    height: 60px;
  }

  &__title-right > *:not(:last-child) {
    margin-right: 30px;
  }

  &__title-left {
    display: flex;
    align-items: center;
  }

  &__title-left > *:not(:last-child) {
    margin-right: 30px;
  }

  &__header-button-wrapper {
    border-radius: 15px;
    border: solid 1px $rfx-light-steel;
    background: white;
    height: 28px;
    display: flex;
    padding: 0 10px;
  }

  &__copy-button {
    flex: 1 1 50%;
    height: 100%;
    border: none;
    outline: none;
    color: $rfx-dark-march;
    white-space: nowrap;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 90px;
    background-color: #fff;
    font-size: 11px;

    &:hover {
      opacity: 0.5;
    }
  }

  &__clear-filters {
    @extend .client-note__copy-button;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: auto;

    span:first-child {
      font-size: 11px;
    }
  }

  &__grid-select {
    flex-basis: 100px;
  }

  &__title-container {
    padding: 14px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__loader {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    height: calc(100vh - 530px);
  }

  &__table {
    position: relative;
    display: flex;
    flex-direction: column;
  }

  &__update-loader {
    position: absolute;
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    background-color: white;
    top: 0;
    opacity: 0.5;
    z-index: 10000;
  }

  &__update-loader-ring {
    margin-top: 200px;
  }

  &__client-notes {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
  }

    &__edit-button {
      flex: 0 0 45px;

      &.common-table__header-cell {
        flex-basis: 0;
      }
    }

  &__row {
    &_name {
      flex-basis: 1.2%;
    }

    &_category {
      flex-basis: 0.9%;
    }

    &_created {
      flex-basis: 1%;
    }
  }

  &__name {

    &.common-table__header-cell {
      flex-basis: 3.3%;

      &::v-deep .multi-select__title-container {
        padding-left: 68px;
      }
    }
  }

  &__button {
    width: 136px;
    padding: 0 20px;
    height: 60px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    border: none;
    outline: none;
    cursor: pointer;
    background-color: $rfx-medium-march;
    color: #fff;
  }

  &__count {
    color: #b2b9be;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
  }
}

.pagination-wrapper {
  display: flex;
  height: 60px;
  margin-top: 10px;
  justify-content: center;
  align-items: center;
  border: 1px solid $rfx-light-steel;
  border-radius: $rfx-default-border-radius;
}

.no-data-block {
  border-bottom: 1px solid $rfx-light-steel;
}

.edit-icon {
  cursor: pointer;
  font-size: 20px;
  justify-self: end;
  display: grid;
  color: $rfx-dark-march;
}
</style>
